// Platform wrapper around the shared column-layout hook
// (@twasta/page-runtime/txn/list). The shared hook takes injected `notify`
// and `persistence` dependencies (the package carries no toast / axios);
// this wrapper curries in react-hot-toast + the designer save APIs so every
// existing call site keeps its original signature and behavior exactly.

import { useMemo } from 'react'
import toast from 'react-hot-toast'

import { saveTransactionObject } from '@/api/designers'
import { modelOverlayPatch } from '@/livepreview/api'
import {
  useGridLayout as useSharedGridLayout,
  type GridLayoutApi, type GridLayoutNotify, type GridLayoutPersistence,
} from '@twasta/page-runtime/txn/list'
import type { TransactionModel } from '@/pages/designers/transaction/types'

export function useGridLayout(opts: {
  model: TransactionModel | null
  setModel: (m: TransactionModel) => void
  project: string
  objName: string
  canEdit: boolean
  /** Where "Apply layout" persists: 'design' bakes width/order into the shared
   *  transaction JSON (designer live preview); 'customize' stores them as a
   *  per-tenant model overlay (run app / downloaded app) so the core design is
   *  untouched. */
  mode?: 'design' | 'customize'
}): GridLayoutApi {
  const notify = useMemo<GridLayoutNotify>(() => ({
    success: (msg) => { toast.success(msg) },
    error: (msg) => { toast.error(msg) },
  }), [])
  const persistence = useMemo<GridLayoutPersistence>(() => ({
    saveDesign: (project, objName, model) =>
      saveTransactionObject(project, objName, { transaction: model }),
    saveOverlay: (project, objName, set) =>
      modelOverlayPatch(project, objName, set, [], 'T'),
  }), [])
  return useSharedGridLayout({ ...opts, notify, persistence })
}
